import { useEffect, useRef, useState, type ReactNode } from "react";
import { checkExpressionSyntax, createSchemaScope, textStyleLabel, type DataSource, type FieldSchema, type InspectorField, type InspectorApi, type Node, type Theme, type RichTextDoc, richTextToPlain, richTextFromTemplate, isRichTextDoc } from "@broadpaper/core";
import { Field, NumberInput, Segmented, Select, Switch, TextArea, TextInput, Button } from "../../ui/primitives.js";
import { Popover } from "../../ui/floating.js";
import { ColorField } from "./ColorField.js";
import { BindingField, useTokenText } from "./BindingField.js";
import { ImageField } from "./ImageField.js";
import { ConditionBuilder } from "./ConditionBuilder.js";
import { ColumnsEditor, SeriesEditor, KeyValuesEditor, SubFormList, type ColumnItem, type SeriesItem, type KeyValueItem } from "./ListEditors.js";
import { DataPicker } from "../DataPicker.js";
import { fontOptions } from "../font-options.js";
import { useSession } from "../../state/context.js";

export interface FieldContext {
  dataSources: DataSource[];
  scopeVars?: Record<string, FieldSchema>;
  theme: Theme;
  textStyles: string[];
  node: Node;
  props: Record<string, unknown>;
  setProps: (patch: Record<string, unknown>) => void;
  allowExpressions: boolean;
}

function getPath(obj: Record<string, unknown>, path: string): unknown {
  let cur: unknown = obj;
  for (const p of path.split(".")) {
    if (cur === null || cur === undefined || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

/** Renders one inspector field. Values flow through `setValue`; every change is one store command. */
export function FieldRenderer({ field, value, onChange, ctx }: { field: InspectorField; value: unknown; onChange: (v: unknown) => void; ctx: FieldContext }) {
  const description = typeof field.description === "function" ? field.description(ctx.props) : field.description;
  const humanise = useTokenText(ctx.dataSources, ctx.scopeVars);
  const wrap = (control: ReactNode, opts: { stacked?: boolean; hint?: ReactNode; error?: ReactNode } = {}) => (
    <Field label={field.label} stacked={opts.stacked ?? field.fullWidth} hint={opts.hint ?? description} error={opts.error}>
      {control}
    </Field>
  );
  const str = typeof value === "string" ? value : value === undefined || value === null ? "" : String(value);

  switch (field.kind) {
    case "text": {
      const tokens = field.allowTokens !== false && ctx.allowExpressions;
      /*
       * A line the template fills in, in a host with no expression editor.
       *
       * The value is `{{ }}` template syntax, and a plain text box over it invites
       * exactly one outcome: a user edits the sentence, breaks the token, and the
       * line goes blank on the document. So the tokens are shown as the names of
       * what they print and the field is read-only, with the block's own text
       * still editable everywhere it is not a token.
       */
      if (!tokens && str.includes("{{")) {
        return wrap(
          <div className="bpe-binding">
            <TextInput value={humanise(str)} onChange={() => undefined} readOnly title="This line fills itself in from your data." />
          </div>,
          { hint: description ?? "Fills itself in from your data." }
        );
      }
      return wrap(
        <div className="bpe-binding">
          <TextInput value={str} onChange={(v) => onChange(v)} lazy placeholder={field.placeholder} />
          {tokens ? (
            <Popover className="bpe-picker" trigger={<Button size="sm" variant="ghost" icon="braces" tooltip="Insert field" />}>
              {(close) => (
                <DataPicker dataSources={ctx.dataSources} scopeVars={ctx.scopeVars} onPick={(expr) => { onChange(`${str}${str && !str.endsWith(" ") ? " " : ""}{{ ${expr} }}`); close(); }} />
              )}
            </Popover>
          ) : null}
        </div>
      );
    }
    case "textarea":
      return wrap(<TextArea value={str} rows={field.rows ?? 3} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} />, { stacked: true });
    case "richtext": {
      const doc: RichTextDoc = isRichTextDoc(value) ? value : richTextFromTemplate(str);
      const plain = richTextToPlain(doc);
      return wrap(
        <TextArea value={plain} rows={4} onChange={(e) => onChange(richTextFromTemplate(e.target.value))} placeholder="Type text, use {{ field }} for data. Double-click the block on the canvas for rich formatting." />,
        { stacked: true, hint: "Double-click the block on the canvas for formatting, lists and links." }
      );
    }
    case "number":
      return wrap(<NumberInput value={typeof value === "number" ? value : null} onChange={(v) => onChange(v)} min={field.min} max={field.max} step={field.step} unit={field.unit} placeholder="auto" />);
    case "toggle":
      return wrap(<Switch checked={!!value} onChange={(v) => onChange(v)} label={field.label} />);
    case "select": {
      const options = field.optionsFrom ? field.optionsFrom(ctx.props) : field.options ?? [];
      return wrap(<Select value={str} onChange={(v) => onChange(coerce(v, options))} options={options.map((o) => ({ value: String(o.value), label: o.label }))} />);
    }
    case "segmented":
      return wrap(<Segmented value={str} onChange={(v) => onChange(coerce(v, field.options))} options={field.options.map((o) => ({ value: String(o.value), label: o.label, icon: o.icon }))} iconOnly={field.options.every((o) => !!o.icon) && field.options.length > 3} ariaLabel={field.label} />);
    case "color":
      return wrap(<ColorField value={str || undefined} onChange={(v) => onChange(v ?? "")} theme={ctx.theme} allowTransparent={field.allowTransparent} />);
    case "font":
      return wrap(<FontSelect value={str} onChange={onChange} theme={ctx.theme} />);
    case "textStyle":
      return wrap(<Select value={str} onChange={(v) => onChange(v || undefined)} placeholder="Default" options={ctx.textStyles.map((s) => ({ value: s, label: textStyleLabel(s) }))} />);
    case "binding":
      return wrap(<BindingField value={str} onChange={(v) => onChange(v)} dataSources={ctx.dataSources} scopeVars={ctx.scopeVars} types={field.valueType ? (Array.isArray(field.valueType) ? field.valueType : [field.valueType]) : undefined} placeholder={field.placeholder} allowExpression={ctx.allowExpressions} />);
    case "collection":
      return wrap(<BindingField value={str} onChange={(v) => onChange(v)} dataSources={ctx.dataSources} scopeVars={ctx.scopeVars} arraysOnly placeholder={field.placeholder} allowExpression={ctx.allowExpressions} />);
    case "expression": {
      /*
       * An expression field in a host with no expression editor.
       *
       * These are the few props that are genuinely a formula rather than a
       * binding — a chart's category label, a table rule's condition — and a mono
       * text box over one is the developer affordance the flag exists to hide. A
       * binding field reads the same value in words and locks it when it is
       * arithmetic, which is the same treatment every other bound prop gets.
       */
      if (!ctx.allowExpressions) {
        return wrap(
          <BindingField value={str} onChange={(v) => onChange(v)} dataSources={ctx.dataSources} scopeVars={ctx.scopeVars} placeholder={field.placeholder} allowExpression={false} allowCalculation={false} />
        );
      }
      const err = str.trim() ? checkExpressionSyntax(str) : null;
      return wrap(
        <div className="bpe-binding">
          <TextInput value={str} onChange={(v) => onChange(v)} lazy mono placeholder={field.placeholder} invalid={!!err} />
          <Popover className="bpe-picker" trigger={<Button size="sm" variant="ghost" icon="braces" tooltip="Pick field" />}>
            {(close) => <DataPicker dataSources={ctx.dataSources} scopeVars={ctx.scopeVars} value={str} onPick={(expr) => { onChange(expr); close(); }} />}
          </Popover>
        </div>,
        { error: err?.message }
      );
    }
    case "image":
      return (
        <ImageFieldRow
          field={field}
          value={str}
          onChange={onChange}
          ctx={ctx}
          render={(control, error) => wrap(control, { error })}
        />
      );
    case "condition":
      return wrap(<ConditionBuilder value={value as never} onChange={(v) => onChange(v ?? "")} dataSources={ctx.dataSources} scopeVars={ctx.scopeVars} allowExpression={ctx.allowExpressions} />, { stacked: true });
    case "columns":
      return wrap(<ColumnsEditor value={(Array.isArray(value) ? value : []) as ColumnItem[]} onChange={onChange} dataSources={ctx.dataSources} scopeVars={ctx.scopeVars} />, { stacked: true });
    case "series":
      return wrap(<SeriesEditor value={(Array.isArray(value) ? value : []) as SeriesItem[]} onChange={onChange} dataSources={ctx.dataSources} scopeVars={ctx.scopeVars} renderColor={(v, set) => <ColorField value={v} onChange={set} theme={ctx.theme} placeholder="Palette" />} />, { stacked: true });
    case "keyValues":
      return wrap(
        <KeyValuesEditor
          value={(Array.isArray(value) ? value : []) as KeyValueItem[]}
          onChange={onChange}
          dataSources={ctx.dataSources}
          scopeVars={ctx.scopeVars}
          allowExpressions={ctx.allowExpressions}
        />,
        { stacked: true }
      );
    case "list":
      return wrap(
        <SubFormList
          value={(Array.isArray(value) ? value : []) as Array<Record<string, unknown>>}
          onChange={onChange}
          fields={field.fields}
          itemLabel={field.itemLabel}
          summary={field.itemSummary ? (item, i) => field.itemSummary!(item, i, ctx.props) : undefined}
          renderField={(f, v, set) => <FieldRenderer field={f} value={v} onChange={set} ctx={ctx} />}
        />,
        { stacked: true }
      );
    case "custom":
      return wrap(<CustomFieldHost field={field} value={value} onChange={onChange} ctx={ctx} />, { stacked: true });
  }
}

function coerce(v: string, options: Array<{ value: string | number | boolean }>): unknown {
  const match = options.find((o) => String(o.value) === v);
  return match ? match.value : v;
}

function FontSelect({ value, onChange, theme }: { value: string; onChange: (v: string) => void; theme: Theme }) {
  return <Select value={value} onChange={onChange} placeholder="Default" options={fontOptions(theme, value)} />;
}

function CustomFieldHost({ field, value, onChange, ctx }: { field: Extract<InspectorField, { kind: "custom" }>; value: unknown; onChange: (v: unknown) => void; ctx: FieldContext }) {
  const session = useSession();
  const ref = useRef<HTMLDivElement>(null);
  const mounted = useRef(false);
  const api = (): InspectorApi => ({
    value,
    props: ctx.props,
    node: ctx.node,
    setValue: onChange,
    setProps: ctx.setProps,
    dataSources: ctx.dataSources,
    schemaScope: createSchemaScope(ctx.dataSources).child(ctx.scopeVars ?? {}),
    theme: ctx.theme
  });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!mounted.current) {
      field.component.mount(el, api());
      mounted.current = true;
    } else field.component.update?.(api());
  });
  useEffect(() => {
    const el = ref.current;
    return () => {
      if (el && mounted.current) field.component.unmount?.(el);
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.component, session]);
  return <div ref={ref} style={{ width: "100%" }} />;
}

/**
 * The image field needs somewhere to keep the last upload failure, and a hook
 * cannot live inside the switch above. It exists only to own that one piece of
 * state and hand it back to the same `wrap` every other field is rendered by,
 * so a rejected file reads like any other invalid value rather than an alert.
 */
function ImageFieldRow({
  field,
  value,
  onChange,
  ctx,
  render
}: {
  field: Extract<InspectorField, { kind: "image" }>;
  value: string;
  onChange: (v: unknown) => void;
  ctx: FieldContext;
  render: (control: ReactNode, error: ReactNode) => ReactNode;
}) {
  const [error, setError] = useState<string | null>(null);
  return render(
    <ImageField
      value={value}
      onChange={(v) => {
        setError(null);
        onChange(v);
      }}
      allowBinding={field.allowBinding !== false}
      dataSources={ctx.dataSources}
      scopeVars={ctx.scopeVars}
      onError={setError}
    />,
    error
  );
}

export { getPath };
